import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  MessageCircle, 
  Lock, 
  Send, 
  DollarSign, 
  MapPin, 
  Phone, 
  Clock, 
  Hash, 
  Building, 
  Mail,
  FileText,
  CheckCircle2
} from 'lucide-react';
import { useOptimizedConversations as useConversations } from '@/hooks/useOptimizedConversations';
import { useAuth } from '@/components/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useUnifiedContact } from '@/contexts/UnifiedContactContext';

const FIELD_CONFIG: Record<string, { label: string; icon: React.FC<{ className?: string }> }> = {
  price: { label: 'Price / Budget', icon: DollarSign },
  location: { label: 'Location Details', icon: MapPin },
  serial: { label: 'Serial Number', icon: Hash },
  hours: { label: 'Hours / Time Data', icon: Clock },
  contact: { label: 'Contact Name', icon: Phone },
  email: { label: 'Email Address', icon: Mail },
  phone: { label: 'Phone Number', icon: Phone },
  company: { label: 'Company Name', icon: Building },
  modules: { label: 'Engine Modules', icon: Clock },
  shipping: { label: 'Shipping Details', icon: MapPin },
};

export const GlobalUnifiedContactModal: React.FC = () => {
  const { isOpen, payload, closeUnifiedContact } = useUnifiedContact();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { createOrGetConversation } = useConversations();
  
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Get available hidden fields
  const availableFields = payload?.hiddenFields 
    ? Object.entries(payload.hiddenFields)
        .filter(([_, isHidden]) => isHidden)
        .map(([field]) => field)
    : [];

  // Get documents that user doesn't have access to yet
  const requestableDocuments = (payload?.confidentialDocuments || []).filter(doc => !doc.has_access);

  const hasInfoToRequest = availableFields.length > 0;
  const hasDocsToRequest = requestableDocuments.length > 0;
  const hasAnyRequests = selectedFields.length > 0 || selectedDocs.length > 0;

  const handleFieldToggle = (field: string) => {
    setSelectedFields(prev =>
      prev.includes(field)
        ? prev.filter(f => f !== field)
        : [...prev, field]
    );
  };

  const handleDocToggle = (docId: string) => {
    setSelectedDocs(prev =>
      prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const selectAllFields = () => {
    setSelectedFields(availableFields);
  };

  const selectAllDocs = () => {
    setSelectedDocs(requestableDocuments.map(d => d.id));
  };

  const handleSubmit = async () => {
    if (!payload) return;
    
    if (!user) {
      toast({
        title: 'Sign in required',
        description: 'Please sign in to contact the seller',
        variant: 'destructive',
      });
      navigate('/auth');
      return;
    }

    if (!message.trim() && !hasAnyRequests) {
      toast({
        title: 'Add content',
        description: 'Please add a message or select information to request',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      // Create or get conversation with asset_type
      const conversationId = await createOrGetConversation(
        payload.listingId,
        payload.sellerId,
        payload.listingTitle,
        payload.assetType || 'helicopter'
      );

      if (!conversationId) {
        throw new Error('Failed to create conversation');
      }

      // Send initial message if provided
      if (message.trim()) {
        await supabase.from('messages').insert({
          conversation_id: conversationId,
          sender_id: user.id,
          content: message.trim(),
          message_type: 'text'
        });
      }

      // Create info access request if fields selected
      if (selectedFields.length > 0) {
        const { data: infoRequest, error: infoError } = await supabase
          .from('info_access_requests')
          .insert({
            conversation_id: conversationId,
            listing_id: payload.listingId,
            requester_id: user.id,
            requested_fields: selectedFields,
            request_message: message || null,
            status: 'pending'
          })
          .select('id')
          .single();

        if (infoError) {
          console.error('Error creating info request:', infoError);
        } else if (infoRequest) {
          // Create a message to display the request card in the conversation
          await supabase.from('messages').insert({
            conversation_id: conversationId,
            sender_id: user.id,
            content: `Requested access to: ${selectedFields.map(f => FIELD_CONFIG[f]?.label || f).join(', ')}`,
            message_type: 'info_request',
            metadata: { request_id: infoRequest.id }
          });
        }
      }

      // Create document access request if docs selected
      if (selectedDocs.length > 0) {
        // Get document names for the message content
        const selectedDocNames = requestableDocuments
          .filter(d => selectedDocs.includes(d.id))
          .map(d => d.document_name || d.document_category);

        const { data: docRequest, error: docError } = await supabase
          .from('document_access_requests')
          .insert({
            conversation_id: conversationId,
            listing_id: payload.listingId,
            requester_id: user.id,
            requested_documents: selectedDocs,
            request_message: message || null,
            status: 'pending'
          })
          .select('id')
          .single();

        if (docError) {
          console.error('Error creating doc request:', docError);
        } else if (docRequest) {
          // Create a message to display the request card in the conversation
          await supabase.from('messages').insert({
            conversation_id: conversationId,
            sender_id: user.id,
            content: `Requested access to documents: ${selectedDocNames.join(', ')}`,
            message_type: 'document_request',
            metadata: { request_id: docRequest.id }
          });
        }
      }

      toast({
        title: hasAnyRequests ? 'Request sent' : 'Message sent',
        description: 'The seller has been notified',
      });
      
      closeUnifiedContact();
      navigate(`/messages?conversation=${conversationId}`);
    } catch (error) {
      console.error('Error sending:', error);
      toast({
        title: 'Error',
        description: 'Failed to send. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Reset state when modal opens with new payload
  useEffect(() => {
    if (isOpen && payload) {
      setSelectedFields(payload.preselectedFields || []);
      setSelectedDocs(payload.preselectedDocuments || []);
      setMessage('');
    }
  }, [isOpen, payload]);

  if (!payload) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeUnifiedContact()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-primary" />
            Contact Seller
          </DialogTitle>
          <DialogDescription>
            Message the seller about "{payload.listingTitle}"
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 px-6">
          <div className="space-y-6 py-4">
            {/* Message Section */}
            <div className="space-y-2">
              <Label htmlFor="global-message" className="text-sm font-medium">
                Your Message
              </Label>
              <Textarea
                id="global-message"
                placeholder="Hi, I'm interested in this listing..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                className="resize-none"
                autoFocus
              />
            </div>

            {/* Info Request Section */}
            {hasInfoToRequest && (
              <>
                <Separator />
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Request Hidden Information
                    </Label>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={selectAllFields}
                      className="text-xs h-7"
                      type="button"
                    >
                      Select All
                    </Button>
                  </div>
                  <div className="grid gap-2">
                    {availableFields.map(field => {
                      const config = FIELD_CONFIG[field];
                      if (!config) return null;
                      const Icon = config.icon;
                      const isSelected = selectedFields.includes(field);

                      return (
                        <button
                          key={field}
                          type="button"
                          className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors text-left w-full ${
                            isSelected 
                              ? 'bg-primary/10 border-primary' 
                              : 'bg-card hover:bg-accent/50 border-border'
                          }`}
                          onClick={() => handleFieldToggle(field)}
                        >
                          <Checkbox
                            id={`field-${field}`}
                            checked={isSelected}
                            onCheckedChange={() => handleFieldToggle(field)}
                            className="border-2 data-[state=checked]:bg-primary data-[state=checked]:border-primary pointer-events-none"
                          />
                          <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span className="flex-1 text-sm">
                            {config.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {/* Document Request Section */}
            {hasDocsToRequest && (
              <>
                <Separator />
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Request Confidential Documents
                    </Label>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={selectAllDocs}
                      className="text-xs h-7"
                      type="button"
                    >
                      Select All
                    </Button>
                  </div>
                  <div className="grid gap-2">
                    {requestableDocuments.map(doc => {
                      const isSelected = selectedDocs.includes(doc.id);
                      
                      return (
                        <button
                          key={doc.id}
                          type="button"
                          className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors text-left w-full ${
                            isSelected 
                              ? 'bg-primary/10 border-primary' 
                              : 'bg-card hover:bg-accent/50 border-border'
                          }`}
                          onClick={() => handleDocToggle(doc.id)}
                        >
                          <Checkbox
                            id={`doc-${doc.id}`}
                            checked={isSelected}
                            onCheckedChange={() => handleDocToggle(doc.id)}
                            className="border-2 data-[state=checked]:bg-primary data-[state=checked]:border-primary pointer-events-none"
                          />
                          <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span className="flex-1 text-sm">
                            {doc.document_name || doc.document_category}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {/* Already have access message */}
            {(payload.confidentialDocuments?.length ?? 0) > 0 && requestableDocuments.length === 0 && (
              <div className="text-center py-4 text-muted-foreground">
                <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
                <p className="text-sm">You have access to all confidential documents</p>
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="flex-shrink-0 gap-2 sm:gap-0 p-6 pt-4 border-t">
          <Button variant="outline" onClick={closeUnifiedContact} type="button">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || (!message.trim() && !hasAnyRequests)}
            type="button"
          >
            {loading ? (
              'Sending...'
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                {hasAnyRequests ? 'Send Request' : 'Send Message'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalUnifiedContactModal;
